'use client';

import React, { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { AlertTriangle, CheckCircle, ShieldAlert, ArrowRight, ArrowLeft } from 'lucide-react';
import { trackToolUsage, trackLeadFormSubmission } from '@/lib/analytics';

const SRC = 'm365-backup';
const FORM_NAME = 'Microsoft 365 Backup Risk Check';

interface AnswerOption {
  label: string;
  /** Risk points contributed by this answer (higher = more exposed) */
  points: number;
  /** Gap surfaced in the result when this option is chosen */
  gap?: string;
}

interface Question {
  id: string;
  question: string;
  helper?: string;
  options: AnswerOption[];
}

const QUESTIONS: Question[] = [
  {
    id: 'has_backup',
    question: 'Do you currently run a dedicated, third-party backup for Microsoft 365?',
    helper: 'Not Microsoft’s native recycle bin or retention policies — a separate backup product or service.',
    options: [
      { label: 'No third-party backup', points: 4, gap: 'No independent backup of Microsoft 365 — a single deletion or ransomware event can be permanent.' },
      { label: 'Not sure', points: 3, gap: 'Backup status is unclear — unverified protection is effectively no protection.' },
      { label: 'Yes, we have third-party backup', points: 0 }
    ]
  },
  {
    id: 'assumes_microsoft',
    question: 'Do you assume Microsoft backs up your data for you?',
    helper: 'Under the Shared Responsibility Model, Microsoft runs the service — your data is your responsibility.',
    options: [
      { label: 'Yes — we assume Microsoft handles it', points: 3, gap: 'Relying on Microsoft to back up your data — their own terms recommend a third-party backup.' },
      { label: 'Not sure who is responsible', points: 2, gap: 'Unclear ownership of backup responsibility across the team.' },
      { label: 'No — we know it’s our responsibility', points: 0 }
    ]
  },
  {
    id: 'retention',
    question: 'How long can you recover deleted mail, files, or sites today?',
    helper: 'Microsoft native retention defaults to roughly 30–93 days with no true point-in-time restore.',
    options: [
      { label: 'Just Microsoft defaults (~30–93 days)', points: 3, gap: 'Retention limited to native windows — older data and point-in-time restores are unavailable.' },
      { label: 'Not sure what our retention is', points: 2, gap: 'Retention settings are unknown — you may be exposed beyond the default window.' },
      { label: 'Years, via dedicated backup', points: 0 }
    ]
  },
  {
    id: 'past_loss',
    question: 'Have you ever lost Microsoft 365 data you could not fully recover?',
    options: [
      { label: 'Yes — we’ve lost data before', points: 3, gap: 'Prior unrecoverable data loss — a recurring gap until independent backup is in place.' },
      { label: 'A close call we barely recovered', points: 2, gap: 'A near-miss recovery indicates the safety net is thin.' },
      { label: 'No data-loss incidents', points: 0 }
    ]
  },
  {
    id: 'ransomware_offboarding',
    question: 'How would you recover from ransomware or a departing employee wiping data?',
    helper: 'Ransomware can encrypt synced OneDrive/SharePoint data; offboarding deletes a user’s mailbox and files.',
    options: [
      { label: 'We don’t have a reliable way to recover', points: 4, gap: 'No reliable recovery path for ransomware or malicious/departing-employee deletion.' },
      { label: 'We sometimes use litigation hold', points: 2, gap: 'Litigation hold is e-discovery, not recovery — slow, partial, and not point-in-time.' },
      { label: 'Independent, immutable backup we can restore', points: 0 }
    ]
  },
  {
    id: 'scale',
    question: 'How many mailboxes / users are in your Microsoft 365 tenant?',
    helper: 'More users means a larger surface area for accidental and malicious data loss.',
    options: [
      { label: '50+ users', points: 2 },
      { label: '11–50 users', points: 1 },
      { label: '1–10 users', points: 0 }
    ]
  }
];

const MAX_POINTS = QUESTIONS.reduce((sum, q) => sum + Math.max(...q.options.map(o => o.points)), 0);

type RiskLevel = 'High' | 'Medium' | 'Low';

function levelFor(score: number): RiskLevel {
  const pct = score / MAX_POINTS;
  if (pct >= 0.55) return 'High';
  if (pct >= 0.25) return 'Medium';
  return 'Low';
}

const LEVEL_STYLES: Record<RiskLevel, { ring: string; badge: string; icon: React.ReactNode; headline: string }> = {
  High: {
    ring: 'border-red-300 dark:border-red-800',
    badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    icon: <ShieldAlert className="w-12 h-12 text-red-600 dark:text-red-400"/>,
    headline: 'Your Microsoft 365 data is exposed to permanent loss.'
  },
  Medium: {
    ring: 'border-amber-300 dark:border-amber-800',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    icon: <AlertTriangle className="w-12 h-12 text-amber-600 dark:text-amber-400"/>,
    headline: 'You have meaningful gaps in your Microsoft 365 protection.'
  },
  Low: {
    ring: 'border-green-300 dark:border-green-800',
    badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    icon: <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400"/>,
    headline: 'You’re in good shape — let’s confirm and close any remaining gaps.'
  }
};

const PLATFORMS = [
  'Microsoft 365 Business (Basic / Standard / Premium)',
  'Microsoft 365 Enterprise (E3 / E5)',
  'Office 365 + on-prem (hybrid Exchange)',
  'Not sure'
];

export default function RiskCheckClient() {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1, then 'result'
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResult, setShowResult] = useState(false);
  const [started, setStarted] = useState(false);

  // Lead form state
  const [form, setForm] = useState({ firstName: '', email: '', company: '', users: '', platform: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadedAt] = useState(() => Date.now());

  const current = QUESTIONS[step];

  const { score, gaps } = useMemo(() => {
    let s = 0;
    const g: string[] = [];
    for (const q of QUESTIONS) {
      const idx = answers[q.id];
      if (typeof idx === 'number') {
        const opt = q.options[idx];
        s += opt.points;
        if (opt.gap) g.push(opt.gap);
      }
    }
    return { score: s, gaps: g };
  }, [answers]);

  const level = levelFor(score);
  const styles = LEVEL_STYLES[level];

  const selectOption = useCallback((qid: string, optIndex: number) => {
    if (!started) {
      setStarted(true);
      trackToolUsage(SRC, 'start');
    }
    trackToolUsage(SRC, 'interact');
    setAnswers(prev => ({ ...prev, [qid]: optIndex }));
    // Auto-advance
    setTimeout(() => {
      setStep(prev => {
        if (prev < QUESTIONS.length - 1) return prev + 1;
        setShowResult(true);
        trackToolUsage(SRC, 'result_generated');
        return prev;
      });
    }, 180);
  }, [started]);

  const resultsSummary = useMemo(() => {
    const lines = [
      `Microsoft 365 Backup Risk: ${level} (score ${score}/${MAX_POINTS})`,
      '',
      'Answers:'
    ];
    for (const q of QUESTIONS) {
      const idx = answers[q.id];
      if (typeof idx === 'number') {
        lines.push(`- ${q.question} → ${q.options[idx].label}`);
      }
    }
    if (gaps.length) {
      lines.push('', 'Identified gaps:');
      gaps.forEach(g => lines.push(`- ${g}`));
    }
    return lines.join('\n');
  }, [answers, gaps, level, score]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');

    let message = resultsSummary + '\n\n';
    message += `Mailboxes / users: ${form.users || 'n/a'}`;
    message += `\nMicrosoft 365 platform: ${form.platform || 'n/a'}`;

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          email: form.email,
          company: form.company,
          message,
          formName: `${FORM_NAME} - Risk: ${level}`,
          src: SRC,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          _t: loadedAt
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackToolUsage(SRC, 'complete');
        trackLeadFormSubmission(FORM_NAME, SRC, undefined, form.email);
      } else {
        const data = await response.json().catch(() => ({} as { error?: string; suggestion?: string }));
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setSubmitStatus('error');
      }
    } catch {
      setErrorMessage('Network error. Please try again.');
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  }, [form, resultsSummary, level, loadedAt]);

  // ---------- Result view ----------
  if (showResult) {
    return (
      <div className={`bg-white dark:bg-gray-800 border-2 ${styles.ring} rounded-2xl p-8 shadow-sm`}>
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">{styles.icon}</div>
          <span className={`inline-block text-sm font-bold px-4 py-1.5 rounded-full mb-4 ${styles.badge}`}>
            Backup Risk: {level}
          </span>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" id="your-backup-risk-result">
            {styles.headline}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Risk score {score} of {MAX_POINTS} based on your retention, recovery, and exposure answers.
          </p>
        </div>

        {gaps.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Gaps we found</h3>
            <ul className="space-y-3">
              {gaps.map((g, i) => (
                <li key={i} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500"/>
                  <span className="text-gray-700 dark:text-gray-300">{g}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {submitStatus === 'success' ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-800 rounded-xl p-6 text-center">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400 mx-auto mb-3"/>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Request received</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-5">
              Your results are on the way to our team. We&apos;ll reach out within 1 business day with a free Microsoft 365 backup assessment tailored to your tenant.
            </p>
            <Link
              href="/services/microsoft-365-backup"
              className="inline-flex items-center gap-2 bg-[#2813e8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1f0fb8] transition-colors"
            >
              See How Managed Backup Works
              <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] rounded-xl p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-white mb-2">Get your free backup assessment</h3>
            <p className="text-white/80 mb-6">
              Send these results to our team and we&apos;ll show you exactly how to close every gap above. No obligation.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="m365-first" className="block text-sm font-medium text-white mb-1">First Name *</label>
                <input id="m365-first" type="text" required value={form.firstName}
                  onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent" placeholder="Jane"/>
              </div>
              <div>
                <label htmlFor="m365-email" className="block text-sm font-medium text-white mb-1">Work Email *</label>
                <input id="m365-email" type="email" required value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent" placeholder="jane@company.com"/>
              </div>
              <div>
                <label htmlFor="m365-users" className="block text-sm font-medium text-white mb-1">Mailboxes / Users *</label>
                <input id="m365-users" type="text" required value={form.users}
                  onChange={e => setForm(p => ({ ...p, users: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent" placeholder="e.g. 45"/>
              </div>
              <div>
                <label htmlFor="m365-platform" className="block text-sm font-medium text-white mb-1">Microsoft 365 Platform *</label>
                <select id="m365-platform" required value={form.platform}
                  onChange={e => setForm(p => ({ ...p, platform: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent">
                  <option value="">Select platform</option>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="m365-company" className="block text-sm font-medium text-white mb-1">Company (Optional)</label>
              <input id="m365-company" type="text" value={form.company}
                onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent" placeholder="Company Name"/>
            </div>

            {submitStatus === 'error' && (
              <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3 text-red-700 text-sm">
                {errorMessage || 'There was an error submitting your request. Please try again.'}
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="mt-6 w-full px-6 py-3 bg-white text-[#2813e8] font-semibold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Submitting...' : 'Send My Results & Get a Free Assessment'}
            </button>
            <p className="text-white/60 text-xs text-center mt-3">
              Your risk results are attached automatically. We typically respond within 1 business day.
            </p>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          Want the offer first?{' '}
          <Link href="/services/microsoft-365-backup" className="text-[#2813e8] dark:text-cyan-400 font-semibold hover:underline">
            Explore managed Microsoft 365 backup
          </Link>.
        </p>
      </div>
    );
  }

  // ---------- Question view ----------
  return (
    <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-8 shadow-sm">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
            Question {step + 1} of {QUESTIONS.length}
          </span>
          <span className="text-sm font-semibold text-[#2813e8] dark:text-cyan-400">
            {Math.round(((step) / QUESTIONS.length) * 100)}% complete
          </span>
        </div>
        <div className="h-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300"
            style={{ width: `${(step / QUESTIONS.length) * 100}%` }}/>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2" id={`question-${current.id}`}>
        {current.question}
      </h2>
      {current.helper && (
        <p className="text-gray-600 dark:text-gray-400 mb-6">{current.helper}</p>
      )}

      <div className="space-y-3">
        {current.options.map((opt, i) => {
          const selected = answers[current.id] === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => selectOption(current.id, i)}
              className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all flex items-center justify-between gap-3 ${
                selected
                  ? 'border-[#2813e8] bg-blue-50 dark:bg-blue-950/40'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-900/40'
              }`}
            >
              <span className="font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
              <ArrowRight className={`w-5 h-5 flex-shrink-0 transition-colors ${selected ? 'text-[#2813e8]' : 'text-gray-300 dark:text-gray-600'}`}/>
            </button>
          );
        })}
      </div>

      {step > 0 && (
        <button
          type="button"
          onClick={() => setStep(s => Math.max(0, s - 1))}
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-[#2813e8] transition-colors"
        >
          <ArrowLeft className="w-4 h-4"/>
          Back
        </button>
      )}
    </div>
  );
}
